// Spec §2.7a — Order module's "Тохиргоо" (8th menu) sub-items 1-9.
// Item 6 (Хүргэлтийн Policy) bundles 4 brainstorm-decision sub-sections via
// accordion in GovernanceConfigTab; items 7/8/9 are separate top-level tabs.
// One concept list header (user 2026-11-04): SegmentedTabs · «?»; the tab lives in the URL (?tab=) like the
// other list pages, and @dehub/help reads it as the `when` context.

import { Box, Group, Tabs } from "@mantine/core"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { HelpIcon, useHelpContext } from "@dehub/theme"
import { SegmentedTabs } from "../ui/segmented-tabs"
import { ChannelLabelsTab } from "./ChannelLabelsTab"
import { ClassificationRuleTab } from "./ClassificationRuleTab"
import { CodConfigTab } from "./CodConfigTab"
import { ConsolidationRuleTab } from "./ConsolidationRuleTab"
import { GovernanceConfigTab } from "./GovernanceConfigTab"
import { HolidayCalendarTab } from "./HolidayCalendarTab"
import { InboundFlowTab } from "./InboundFlowTab"
import { NotificationTemplateTab } from "./NotificationTemplateTab"
import { SizeScaleTab } from "./SizeScaleTab"

const TABS = ["size-scale", "classification", "consolidation", "holidays", "notification", "governance", "cod", "channel-labels", "inbound"] as const
type SettingsTab = (typeof TABS)[number]

const TAB_LABEL: Record<SettingsTab, string> = {
  "size-scale": "page.tabs.sizeScale",
  classification: "page.tabs.classification",
  consolidation: "page.tabs.consolidation",
  holidays: "page.tabs.holidays",
  notification: "page.tabs.notification",
  governance: "page.tabs.governance",
  cod: "page.tabs.cod",
  "channel-labels": "page.tabs.channelLabels",
  inbound: "page.tabs.inbound",
}

/** `order.settings.section.*` row shown by the «?» next to the tabs (the tab's main section). */
const TAB_SECTION: Record<SettingsTab, string> = {
  "size-scale": "size-scale",
  classification: "classification",
  consolidation: "consolidation",
  holidays: "working-schedule",
  notification: "notification",
  governance: "governance-core",
  cod: "cod-config",
  "channel-labels": "channel-labels",
  inbound: "inbound-internal",
}

const isTab = (v: string | null): v is SettingsTab => !!v && (TABS as readonly string[]).includes(v)

export function Settings() {
  const { t } = useTranslation("settings")
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get("tab")
  const activeTab: SettingsTab = isTab(requested) ? requested : "size-scale"
  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value === "size-scale") next.delete("tab")
    else next.set("tab", value)
    setSearchParams(next, { replace: true })
  }

  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({ when: activeTab })
  }, [activeTab, setHelpContext])

  return (
    <Tabs value={activeTab} keepMounted={false}>
      <Group gap="sm" align="center" wrap="nowrap">
        {/* The 9 tabs scroll sideways instead of wrapping (O36). */}
        <Box style={{ overflowX: "auto", maxWidth: "100%" }}>
          <SegmentedTabs value={activeTab} onChange={onTabChange} data={TABS.map((tab) => ({ value: tab, label: t(TAB_LABEL[tab]) }))} />
        </Box>
        <HelpIcon helpKey={`order.settings.section.${TAB_SECTION[activeTab]}`} variant="section" />
      </Group>

      <Tabs.Panel value="size-scale" pt="md">
        <SizeScaleTab />
      </Tabs.Panel>
      <Tabs.Panel value="classification" pt="md">
        <ClassificationRuleTab />
      </Tabs.Panel>
      <Tabs.Panel value="consolidation" pt="md">
        <ConsolidationRuleTab />
      </Tabs.Panel>
      <Tabs.Panel value="holidays" pt="md">
        <HolidayCalendarTab />
      </Tabs.Panel>
      <Tabs.Panel value="notification" pt="md">
        <NotificationTemplateTab />
      </Tabs.Panel>
      <Tabs.Panel value="governance" pt="md">
        <GovernanceConfigTab />
      </Tabs.Panel>
      <Tabs.Panel value="cod" pt="md">
        <CodConfigTab />
      </Tabs.Panel>
      <Tabs.Panel value="channel-labels" pt="md">
        <ChannelLabelsTab />
      </Tabs.Panel>
      <Tabs.Panel value="inbound" pt="md">
        <InboundFlowTab />
      </Tabs.Panel>
    </Tabs>
  )
}
